import { useCallback, useEffect, useRef, useState } from "react";
import {
  DeckUndoStack,
  type SurfDeck,
} from "@vida/surf-core";
import { useSurfLibrary } from "./useSurfLibrary";

const SAVE_DEBOUNCE_MS = 1200;

export function useDeckEditor(documentId: string) {
  const { getDeck, saveDeck } = useSurfLibrary();
  const row = getDeck(documentId);
  const [deck, setDeck] = useState<SurfDeck | null>(() =>
    row?.deck ? structuredClone(row.deck) : null,
  );
  const [slideIndex, setSlideIndex] = useState(0);
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [historyTick, setHistoryTick] = useState(0);
  const undoRef = useRef(new DeckUndoStack());
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (row?.deck && !initializedRef.current) {
      initializedRef.current = true;
      setDeck(structuredClone(row.deck));
      undoRef.current.reset(row.deck);
      setHistoryTick((t) => t + 1);
    }
  }, [row]);

  const slide = deck?.slides[slideIndex] ?? null;

  const commit = useCallback(
    (next: SurfDeck) => {
      setDeck(next);
      undoRef.current.push(next);
      setHistoryTick((t) => t + 1);
      setDirty(true);
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        setSaving(true);
        void saveDeck(next, documentId)
          .then(() => setDirty(false))
          .finally(() => setSaving(false));
      }, SAVE_DEBOUNCE_MS);
    },
    [documentId, saveDeck],
  );

  const undo = useCallback(() => {
    if (!deck) return;
    const prev = undoRef.current.undo(deck);
    if (prev) {
      setDeck(prev);
      setHistoryTick((t) => t + 1);
      setDirty(true);
    }
  }, [deck]);

  const redo = useCallback(() => {
    const next = undoRef.current.redo();
    if (next) {
      setDeck(next);
      setHistoryTick((t) => t + 1);
      setDirty(true);
    }
  }, []);

  useEffect(
    () => () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    },
    [],
  );

  void historyTick;

  const canUndo = undoRef.current.canUndo();
  const canRedo = undoRef.current.canRedo();

  return {
    row,
    deck,
    slide,
    slideIndex,
    setSlideIndex,
    selectedLayerId,
    setSelectedLayerId,
    commit,
    undo,
    redo,
    canUndo,
    canRedo,
    saving,
    dirty,
  };
}
